import type { CacheNames } from "../../../utils/sw-cache";

export default interface ModuleAssetsCache {
  cacheAsset(
    moduleId: string,
    assetUrl: string,
    abortSignal?: AbortSignal,
  ): Promise<void>;
  isAssetCached(moduleId: string, assetUrl: string): Promise<boolean>;
  uncacheAllAssets(moduleId: string): Promise<void>;
}

export class WebModuleAssetsCache implements ModuleAssetsCache {
  constructor(
    private cacheStorage: CacheStorage,
    private cacheName: CacheNames,
    private fetch = window.fetch.bind(window),
  ) {}

  async cacheAsset(
    moduleId: string,
    assetUrl: string,
    abortSignal?: AbortSignal,
  ): Promise<void> {
    const cache = await this.openCache();
    const isAlreadyCached = await this.isAssetCached(moduleId, assetUrl);
    if (!isAlreadyCached) {
      const response = await this.fetch(assetUrl, {
        signal: abortSignal,
      });
      // TODO: check if response header approach to tagging cache entries
      // is preferable.
      await cache.put(`${assetUrl}&moduleId=${moduleId}`, response);
    }
  }

  async isAssetCached(moduleId: string, assetUrl: string): Promise<boolean> {
    const cache = await this.openCache();
    const cachedResponse = await cache.match(
      `${assetUrl}&moduleId=${moduleId}`,
    );
    return cachedResponse !== undefined;
  }

  async uncacheAllAssets(moduleId: string): Promise<void> {
    const cache = await this.openCache();
    const cachedRequests = await cache.keys();
    await Promise.all(
      cachedRequests
        .filter(({ url }) => url.includes(`moduleId=${moduleId}`))
        .map(({ url }) => cache.delete(url)),
    );
  }

  private cache: Cache | null = null;
  private async openCache(): Promise<Cache> {
    if (!this.cache) {
      this.cache = await this.cacheStorage.open(this.cacheName);
    }
    return this.cache!;
  }
}
